/**
 * Workloads API Service
 * Handles workload definitions, applications, and installer management
 */

import apiClient from './api';
import { ApiResponse, WorkloadDefinition, Application, InstallerMetadata } from './types';
import SparkMD5 from 'spark-md5';
import { UploadHttpError } from './uploadHttpError';

/**
 * Workloads API
 */
export const workloadsApi = {
  /**
   * Get list of all active workloads
   */
  async list(includeInactive = false): Promise<WorkloadDefinition[]> {
    const response = await apiClient.get<ApiResponse<WorkloadDefinition[]>>('/workloads', {
      params: { includeInactive },
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch workloads');
    }
    return response.data.data;
  },

  /**
   * Get workload details by ID
   */
  async getById(workloadId: string): Promise<WorkloadDefinition> {
    const response = await apiClient.get<ApiResponse<WorkloadDefinition>>(
      `/workloads/${workloadId}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch workload details');
    }
    return response.data.data;
  },

  /**
   * Create new workload definition
   */
  async create(
    workload: Omit<WorkloadDefinition, 'id' | 'pk' | 'createdAt' | 'createdBy' | 'modifiedAt' | 'modifiedBy'>
  ): Promise<WorkloadDefinition> {
    const response = await apiClient.post<ApiResponse<WorkloadDefinition>>('/workloads', workload);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to create workload');
    }
    return response.data.data;
  },

  /**
   * Update workload metadata
   */
  async update(
    workloadId: string,
    updates: Partial<WorkloadDefinition>
  ): Promise<WorkloadDefinition> {
    const response = await apiClient.patch<ApiResponse<WorkloadDefinition>>(
      `/workloads/${workloadId}`,
      updates
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to update workload');
    }
    return response.data.data;
  },

  /**
   * Soft-delete workload (sets isActive=false)
   */
  async delete(workloadId: string): Promise<{ workloadId: string }> {
    const response = await apiClient.delete<ApiResponse<{ workloadId: string }>>(
      `/workloads/${workloadId}`
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to delete workload');
    }
    return response.data.data;
  },
};

/**
 * Applications API (nested under workloads)
 */
export const applicationsApi = {
  /**
   * Add application to workload
   */
  async add(
    workloadId: string,
    application: {
      id: string;
      displayName: string;
      category: 'primary' | 'support' | 'optional';
      installConfig: {
        silentArgs: string;
        requiresReboot: boolean;
        installOrder: number;
      };
    }
  ): Promise<Application> {
    const response = await apiClient.post<ApiResponse<Application>>(
      `/workloads/${workloadId}/applications`,
      application
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to add application');
    }
    return response.data.data;
  },

  /**
   * Update application configuration
   */
  async update(
    workloadId: string,
    applicationId: string,
    updates: Partial<Application>
  ): Promise<Application> {
    const response = await apiClient.patch<ApiResponse<Application>>(
      `/workloads/${workloadId}/applications/${applicationId}`,
      updates
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to update application');
    }
    return response.data.data;
  },
};

/**
 * Installers API
 */
export const installersApi = {
  /**
   * Get list of installers with optional filters
   */
  async list(params?: {
    workloadId?: string;
    applicationId?: string;
    status?: 'pending' | 'validated' | 'active' | 'deprecated';
    isLatest?: boolean;
  }): Promise<InstallerMetadata[]> {
    const response = await apiClient.get<ApiResponse<InstallerMetadata[]>>('/installers', {
      params,
    });
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to fetch installers');
    }
    return response.data.data;
  },

  /**
   * Request upload URL for installer (Step 1 of upload flow).
   *
   * NC-963: `sizeBytes` (optional) is the client-declared file size. The server
   * scales the write-SAS TTL by it (60-min base, +60 per full 5 GiB, cap 240)
   * so multi-GB uploads — browser or guided-azcopy — can't outlive the SAS. The
   * response's `expiresInMinutes` surfaces that effective budget to the UI (AC4).
   */
  async requestUploadUrl(params: {
    workloadId: string;
    applicationId: string;
    fileName: string;
    version: string;
    sizeBytes?: number;
  }): Promise<{
    uploadUrl: string;
    blobName: string;
    installerId: string;
    expiresInMinutes: number;
  }> {
    const response = await apiClient.post<
      ApiResponse<{ uploadUrl: string; blobName: string; installerId: string; expiresInMinutes: number }>
    >('/installers-upload-url', params);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to request upload URL');
    }
    return response.data.data;
  },

  /**
   * Upload file to blob storage (Step 2 of upload flow).
   * Uploads directly to Azure Blob Storage via the write-only SAS URL.
   *
   * NC-1001: sets the `x-ms-blob-content-md5` header so Azure persists the
   * blob's Content-MD5 property. Without it a single Put Blob stores no
   * Content-MD5, so the server-side confirm (verifyBlobIntegrity) reads an
   * empty property and 400s every upload. `md5Base64` MUST be base64 of the
   * raw 16-byte digest (see computeMD5), not hex.
   *
   * NC-963: an optional `signal` lets the caller abort a browser upload
   * mid-flight (dialog "Abbrechen") — the XHR is aborted and the promise
   * rejects with 'Upload cancelled' rather than silently continuing.
   */
  async uploadFile(
    uploadUrl: string,
    file: File,
    md5Base64: string,
    onProgress?: (progress: number) => void,
    signal?: AbortSignal
  ): Promise<void> {
    const xhr = new XMLHttpRequest();

    return new Promise((resolve, reject) => {
      // NC-963: honour an already-aborted signal before opening the request.
      if (signal?.aborted) {
        reject(new Error('Upload cancelled'));
        return;
      }

      const onAbort = () => xhr.abort();
      signal?.addEventListener('abort', onAbort);

      const cleanup = () => signal?.removeEventListener('abort', onAbort);

      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const percentComplete = (event.loaded / event.total) * 100;
          onProgress(percentComplete);
        }
      });

      // NC-1722: typed rejections so InstallersPage can hand the failure to
      // the error reporter (the PUT bypasses the apiClient interceptor). The
      // message texts are unchanged; a user cancel stays a plain Error.
      xhr.addEventListener('load', () => {
        cleanup();
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new UploadHttpError(xhr.status, uploadUrl, `Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        cleanup();
        reject(new UploadHttpError(0, uploadUrl, 'Upload failed due to network error'));
      });

      xhr.addEventListener('abort', () => {
        cleanup();
        reject(new Error('Upload cancelled'));
      });

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
      if (md5Base64) {
        // NC-1001: persist the blob's Content-MD5 so confirm can verify integrity.
        xhr.setRequestHeader('x-ms-blob-content-md5', md5Base64);
      }
      xhr.send(file);
    });
  },

  /**
   * Confirm upload and create metadata (Step 3 of upload flow).
   *
   * NC-963: `md5Hash` is optional. Omit it for the guided-azcopy path — the
   * server then verifies integrity against the blob's own Content-MD5 (set by
   * `azcopy --put-md5`) instead of a client SparkMD5, so a multi-GB file that
   * never passed through the browser is still verified. Passing an empty string
   * still 400s server-side; omit the key entirely (undefined is dropped by
   * JSON serialization) for the azcopy path.
   */
  async confirmUpload(params: {
    installerId: string;
    md5Hash?: string;
    sizeBytes: number;
    markAsLatest: boolean;
  }): Promise<{
    workloadId: string;
    applicationId: string;
  }> {
    const response = await apiClient.post<
      ApiResponse<{ workloadId: string; applicationId: string }>
    >('/installers-confirm', params);
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to confirm upload');
    }
    return response.data.data;
  },

  /**
   * Delete installer from storage and metadata
   */
  async delete(installerId: string, workloadId: string): Promise<{ installerId: string; blobName: string }> {
    const response = await apiClient.delete<ApiResponse<{ installerId: string; blobName: string }>>(
      `/installers/${installerId}`,
      {
        params: { workloadId },
      }
    );
    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error || 'Failed to delete installer');
    }
    return response.data.data;
  },

  /**
   * Compute the MD5 hash of a file for integrity verification using SparkMD5.
   *
   * NC-1001: returns **base64** of the raw 16-byte digest (e.g. empty file →
   * "1B2M2Y8AsgTpgAmY7PhCfg=="), matching Azure's Content-MD5 blob property and
   * the server-side `verifyBlobIntegrity` contract (which compares base64).
   * A hex digest (SparkMD5's default `end()`) never matches and 400s confirm.
   */
  async computeMD5(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const blobSlice = File.prototype.slice;
      const chunkSize = 2097152; // Read in chunks of 2MB
      const chunks = Math.ceil(file.size / chunkSize);
      let currentChunk = 0;
      const spark = new SparkMD5.ArrayBuffer();
      const fileReader = new FileReader();

      fileReader.onload = (e) => {
        spark.append(e.target?.result as ArrayBuffer);
        currentChunk++;

        if (currentChunk < chunks) {
          loadNext();
        } else {
          // end(true) → raw binary string; btoa → base64 (Azure Content-MD5 form).
          resolve(btoa(spark.end(true)));
        }
      };

      fileReader.onerror = () => {
        reject(new Error('Failed to read file for MD5 computation'));
      };

      function loadNext() {
        const start = currentChunk * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        fileReader.readAsArrayBuffer(blobSlice.call(file, start, end));
      }

      loadNext();
    });
  },
};
